import { useEffect, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import {
  ArrowRight,
  BookOpen,
  Check,
  ChevronRight,
  Clipboard,
  FileText,
  Lightbulb,
  LockKeyhole,
  Network,
  Route,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLiveResource } from "@/hooks/useLiveResource";
import { useChat } from "@/components/chat/ChatWorkspaceContext";
import { sourceDate } from "./BrainSourceDialog";
export function playbookPrompt(book, workspaceName) {
  return `${book.prompt}\n\nWorkspace: ${workspaceName}.\nPlaybook: ${book.title} v${book.version}. Knowledge package: ${book.packageName}@${book.packageVersion}.\nSources: ${book.sources.map((s) => `${s.title} — ${s.author}, ${s.course}; source ${s.id}; passages ${s.passageIds.join(", ")}`).join("\n")}\nThis is a proposed assignment. Retrieve authorized sources and check current brand context before applying it.`;
}
export default function PlaybookReader({
  id,
  initialTab = "method",
  clientSlug,
  workspaceName,
  agentName,
  entries,
  onClose,
  onLocate,
}) {
  const resource = useLiveResource("getBrainPlaybook", [id], {
    clearOnError: true,
  });
  const book =
    !resource.error &&
    resource.data?.clientSlug === clientSlug &&
    resource.data?.playbook?.id === id
      ? resource.data.playbook
      : null;
  const [tab, setTab] = useState(initialTab),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false);
  const chat = useChat(),
    navigate = useNavigate(),
    body = useRef(null),
    mounted = useRef(true);
  const returnFocus = useRef(
    typeof document !== "undefined" ? document.activeElement : null,
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => {
    body.current?.scrollTo?.({ top: 0 });
  }, [tab]);
  const facts = entries
    .filter((e) => ["memory", "preference"].includes(e.kind))
    .sort(
      (a, b) =>
        Number(
          /PDP|brand guide|email|social|pricing|promotion/i.test(b.title),
        ) -
        Number(/PDP|brand guide|email|social|pricing|promotion/i.test(a.title)),
    );
  const skills = entries
    .filter(
      (e) =>
        e.kind === "skill" &&
        /offer|paid traffic|pdp|lifecycle|retention/i.test(e.title),
    )
    .slice(0, 4);
  const prepare = async () => {
    if (!book || busy) return;
    setBusy(true);
    setMessage("");
    try {
      const prompt = playbookPrompt(book, workspaceName);
      if (chat?.controller) {
        const session = await chat.controller.createSession();
        if (!mounted.current) return;
        if (!session) throw new Error("session");
        chat.controller.updateDraft(session, prompt);
        navigate("/agent/chat");
      } else {
        await navigator.clipboard.writeText(prompt);
        if (mounted.current)
          setMessage("Assignment copied. Paste it into your agent’s chat.");
      }
    } catch {
      if (mounted.current)
        setMessage("Could not prepare the assignment. Please try again.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="solar-dialog-overlay" />
        <Dialog.Content
          className="atlas-reader"
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            returnFocus.current?.focus?.();
          }}
        >
          <aside className="atlas-reader-rail">
            <div className="atlas-publisher">
              <BookOpen size={25} />
              <span>
                SMART MARKETER<small>KNOWLEDGE PLAYBOOK</small>
              </span>
            </div>
            <div className="atlas-reader-emblem" aria-hidden="true">
              <Route size={54} strokeWidth={1} />
              <i />
              <i />
              <i />
            </div>
            <p className="atlas-rail-caption">
              Expertise, connected
              <br />
              to your business.
            </p>
            <nav aria-label="Playbook sections">
              {[
                ["method", "The method", Route],
                ["example", "Worked example", Lightbulb],
                ["brand", `For ${workspaceName}`, LockKeyhole],
                ["sources", "Sources & evidence", Network],
              ].map(([key, label, Icon]) => (
                <button
                  type="button"
                  key={key}
                  onClick={() => setTab(key)}
                  aria-pressed={tab === key}
                >
                  <Icon size={17} />
                  {label}
                  <ChevronRight size={12} />
                </button>
              ))}
            </nav>
            <div className="atlas-reader-credit">
              <span>Prepared for</span>
              <strong>{agentName}</strong>
              <small>Your Hermes agent</small>
            </div>
          </aside>
          <div className="atlas-reader-main" ref={body}>
            <Dialog.Title className="sr-only">
              {book?.title || "Knowledge playbook"}
            </Dialog.Title>
            <Dialog.Description className="sr-only">
              A source-backed method, worked example and proposed application to
              your brand.
            </Dialog.Description>
            {!book ? (
              <div className="atlas-reader-state">
                <BookOpen size={32} />
                <h2>
                  {resource.status === "loading"
                    ? "Opening the playbook…"
                    : "This playbook is unavailable"}
                </h2>
                <p>
                  {resource.status === "loading"
                    ? "Checking your connected knowledge."
                    : "Access may have changed, or the source could not be reached."}
                </p>
                {resource.status !== "loading" && (
                  <button
                    className="solar-primary"
                    onClick={() => resource.reload().catch(() => {})}
                  >
                    Try again
                  </button>
                )}
              </div>
            ) : (
              <>
                <header className="atlas-reader-heading">
                  <div className="atlas-reader-meta">
                    <span>OFFERS & FUNNELS</span>
                    <span>{book.readMinutes} MIN READ</span>
                    <span>v{book.version}</span>
                  </div>
                  <h2>{book.title}</h2>
                  <p>{book.summary}</p>
                  <div className="atlas-reader-byline">
                    <span className="atlas-author-mark">MP</span>
                    <span>
                      Based on lessons by{" "}
                      <strong>{book.sources[0]?.author}</strong>
                      <small>
                        {book.sources[0]?.course} · Editorial adaptation
                      </small>
                    </span>
                  </div>
                </header>
                {tab === "method" && (
                  <article className="atlas-reader-content">
                    <section className="atlas-outcome">
                      <span>WHAT YOU’LL WALK AWAY WITH</span>
                      <p>{book.outcome}</p>
                    </section>
                    <section>
                      <h3>When to use it</h3>
                      <p>{book.whenToUse}</p>
                    </section>
                    <section>
                      <h3>Why it works</h3>
                      <p>{book.mechanism}</p>
                    </section>
                    <section>
                      <span className="atlas-eyebrow">
                        FROM PRINCIPLE TO PRACTICE
                      </span>
                      <h3>The method, step by step</h3>
                      <ol className="atlas-method-steps">
                        {book.steps.map((step, i) => (
                          <li key={step.title}>
                            <span>{String(i + 1).padStart(2, "0")}</span>
                            <div>
                              <h4>{step.title}</h4>
                              <p>{step.body}</p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </section>
                    <section>
                      <h3>Choose the right move</h3>
                      <div className="atlas-decisions">
                        {book.decisions.map((d) => (
                          <div key={d.situation}>
                            <strong>{d.situation}</strong>
                            <ArrowRight size={16} />
                            <p>{d.action}</p>
                          </div>
                        ))}
                      </div>
                    </section>
                    <section className="atlas-quality">
                      <div>
                        <h3>What good looks like</h3>
                        {book.checks.map((c) => (
                          <p key={c}>
                            <Check size={15} />
                            {c}
                          </p>
                        ))}
                      </div>
                      <div>
                        <h3>Watch out for</h3>
                        {book.pitfalls.map((c) => (
                          <p key={c}>
                            <span>—</span>
                            {c}
                          </p>
                        ))}
                      </div>
                    </section>
                    <button
                      className="atlas-next-section"
                      onClick={() => setTab("example")}
                    >
                      See the method in practice
                      <ArrowRight size={18} />
                    </button>
                  </article>
                )}
                {tab === "example" && (
                  <article className="atlas-reader-content">
                    <span className="atlas-eyebrow">A WORKED EXAMPLE</span>
                    <h3>{book.example.title}</h3>
                    <p>{book.example.context}</p>
                    <div className="atlas-example-flow">
                      {book.example.steps.map((s, i) => (
                        <section key={s.title}>
                          <span>0{i + 1}</span>
                          <h4>{s.title}</h4>
                          <p>{s.body}</p>
                        </section>
                      ))}
                    </div>
                    <section className="atlas-outcome">
                      <span>THE TAKEAWAY</span>
                      <p>{book.example.takeaway}</p>
                    </section>
                    <button
                      className="atlas-next-section"
                      onClick={() => setTab("brand")}
                    >
                      Connect this to {workspaceName}
                      <ArrowRight size={18} />
                    </button>
                  </article>
                )}
                {tab === "brand" && (
                  <article className="atlas-reader-content">
                    <span className="atlas-eyebrow">
                      PRIVATE BRAND CONTEXT · PROPOSED APPLICATION
                    </span>
                    <h3>Make the method yours.</h3>
                    <p>{book.application.intro}</p>
                    <section>
                      <h3>Context your agent has saved</h3>
                      <p className="atlas-supporting">
                        Saved notes from your workspace. Select one to inspect
                        its original context before applying it.
                      </p>
                      <div className="atlas-brand-facts">
                        {facts.slice(0, 6).map((e) => (
                          <button key={e.id} onClick={() => onLocate(e.id)}>
                            <span>
                              <FileText size={15} />
                              {e.title}
                            </span>
                            <p>{e.content || e.summary}</p>
                            <small>
                              {e.sourceLabel} · Source modified{" "}
                              {sourceDate(e.updatedAt)}
                            </small>
                            <ChevronRight size={16} />
                          </button>
                        ))}
                      </div>
                      {!facts.length && (
                        <p>
                          No saved memory is available in this snapshot. The
                          agent should ask for the inputs below.
                        </p>
                      )}
                    </section>
                    {skills.length > 0 && (
                      <section>
                        <h3>Relevant enabled skills</h3>
                        <p className="atlas-supporting">
                          Reported by your agent’s runtime. Select a skill to
                          inspect its description.
                        </p>
                        <div className="atlas-skill-links">
                          {skills.map((e) => (
                            <button key={e.id} onClick={() => onLocate(e.id)}>
                              <Clipboard size={15} />
                              {e.title}
                              <ChevronRight size={13} />
                            </button>
                          ))}
                        </div>
                      </section>
                    )}
                    <section>
                      <h3>Inputs to confirm</h3>
                      <ul className="atlas-inputs">
                        {book.application.inputs.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </section>
                    <section>
                      <h3>A proposed assignment for {agentName}</h3>
                      <ol className="atlas-application-steps">
                        {book.application.steps.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ol>
                    </section>
                    <section className="atlas-outcome">
                      <span>EXPECTED DELIVERABLE</span>
                      <p>{book.application.output}</p>
                    </section>
                    <p className="atlas-supporting">
                      This is a suggested application. This view does not report
                      previous use of the method or campaign results.
                    </p>
                  </article>
                )}
                {tab === "sources" && (
                  <article className="atlas-reader-content">
                    <span className="atlas-eyebrow">THE SOURCE TRAIL</span>
                    <h3>Where this method comes from.</h3>
                    <p>
                      This editorial playbook was checked against the indexed
                      lesson passages below. Source examples are paraphrased;
                      the brand assignment is a proposal. It is not the original
                      course transcript.
                    </p>
                    {book.sources.map((s) => (
                      <section className="atlas-source-citation" key={s.id}>
                        <BookOpen size={24} />
                        <div>
                          <span>SMART MARKETER · LESSON</span>
                          <h4>{s.title}</h4>
                          <p>
                            {s.author} · {s.course}
                          </p>
                          <small>
                            Lesson date {s.date} · {s.passageIds.length} indexed
                            passages
                          </small>
                          <details>
                            <summary>Inspect source references</summary>
                            <p>
                              Source: <code>{s.id}</code>
                            </p>
                            {s.passageIds.map((id) => (
                              <code key={id}>{id}</code>
                            ))}
                          </details>
                        </div>
                      </section>
                    ))}
                    <section>
                      <h3>Connected knowledge version</h3>
                      <p>
                        {book.packageName} · v{book.packageVersion}
                      </p>
                      <p className="atlas-supporting">
                        Playbook v{book.version} · Source-checked editorial
                        adaptation. Access is checked against your workspace’s
                        connected package.
                      </p>
                    </section>
                    <section>
                      <h3>Evidence of application</h3>
                      <p>
                        No execution history is attached to this playbook. Saved
                        brand context is available in the brand view; a recorded
                        application needs a linked conversation or artifact.
                      </p>
                    </section>
                  </article>
                )}
                <footer className="atlas-reader-footer">
                  <span>
                    <LockKeyhole size={13} />
                    Prepared for your workspace
                  </span>
                  <button
                    className="solar-primary"
                    disabled={busy}
                    onClick={prepare}
                  >
                    {busy ? "Preparing…" : `Work on this with ${agentName}`}
                    <ArrowRight size={15} />
                  </button>
                  <small role="status">
                    {message ||
                      "Opens an editable draft. You decide when to send."}
                  </small>
                </footer>
              </>
            )}
          </div>
          <Dialog.Close
            className="atlas-reader-close"
            aria-label="Close playbook"
          >
            <X size={20} />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
